import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import LaunchIcon from '@mui/icons-material/Launch'
import PhoneIcon from '@mui/icons-material/Phone'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { getProspect, getReportDownloadUrl } from 'src/supportPortal/services/patientProspectsApi'
import { toast } from 'react-toastify'

// Detail surface for a single Patient Prospect. Opens as a right-side
// drawer from the Patient Prospects list. Pulls the full bundle (prospect
// + linked CareIntakeCase + recommendation) on every open so the staff
// member sees up-to-the-second state before deciding to promote / close.
//
// The action buttons in the footer mirror the row-level Promote / Close,
// but here the staff member has had the chance to read the full intake
// — concerns, duration, prior-support history, free-text "in your own
// words", subject info, medical history summary, and the report list.
//
// We deliberately don't expose download URLs for reports yet — that
// requires the media-fetch signed-URL flow which lives outside this
// dialog's scope. File metadata (name, size, type, uploaded date) is
// enough for staff to know what's been uploaded; previewing/downloading
// is a follow-up that needs its own access-control review.

function statusChip(status) {
  const palette = {
    SUBMITTED: 'primary',
    IN_TRIAGE: 'info',
    AWAITING_BOOKING: 'warning',
    PROMOTED: 'success',
    CLOSED: 'default',
  }
  return <Chip size="small" label={status || '—'} color={palette[status] || 'default'} />
}

function Field({ label, value, multiline = false }) {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) return null
  const rendered = Array.isArray(value)
    ? value.map((v, i) => (
        <Chip key={i} size="small" label={String(v)} sx={{ mr: 0.5, mb: 0.5 }} />
      ))
    : multiline
      ? <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{String(value)}</Typography>
      : <Typography variant="body2">{String(value)}</Typography>
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 11 }}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.25 }}>{rendered}</Box>
    </Box>
  )
}

function Section({ title, children }) {
  return (
    <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle2" sx={{ mb: 1.25, color: 'text.secondary', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.6 }}>
        {title}
      </Typography>
      {children}
    </Box>
  )
}

export default function ProspectDetailDrawer({ open, prospectSummary, onClose, onPromote, onReject }) {
  const [bundle, setBundle] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [openingReportId, setOpeningReportId] = useState(null)

  const openReport = async (userId, reportId) => {
    if (!userId || !reportId) return
    // Open a window synchronously (so it isn't blocked by the popup
    // blocker once the async URL resolves), then redirect it once we
    // have the presigned URL. Failure or null URL closes the window.
    const w = window.open('', '_blank')
    setOpeningReportId(reportId)
    try {
      const res = await getReportDownloadUrl(userId, reportId)
      const url = res?.data?.url || res?.data?.data?.url
      if (url) {
        if (w) w.location.href = url
        else window.open(url, '_blank')
      } else {
        if (w) w.close()
        toast.error('This file isn’t downloadable yet (likely stored locally in dev).')
      }
    } catch (e) {
      if (w) w.close()
      toast.error(e?.message || 'Could not open this report.')
    } finally {
      setOpeningReportId(null)
    }
  }

  // Re-fetch every time the drawer opens for a different userId.
  useEffect(() => {
    if (!open || !prospectSummary?.userId) {
      setBundle(null)
      setError('')
      return
    }
    let cancelled = false
    setLoading(true)
    setError('')
    getProspect(prospectSummary.userId)
      .then((res) => {
        if (cancelled) return
        if (res?.status >= 200 && res?.status < 300) {
          setBundle(res.data || null)
        } else {
          setError(res?.response?.data?.message || res?.data?.message || 'Could not load this prospect.')
        }
      })
      .catch((e) => {
        if (cancelled) return
        setError(e?.message || 'Could not load this prospect.')
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, prospectSummary?.userId])

  const prospect = bundle?.prospect || prospectSummary || null
  const careCase = bundle?.careCase || null
  const reco = bundle?.recommendation || null
  const intake = prospect?.intakeAnswers || {}
  const subject = intake.subject || null
  const reports = prospect?.reports || []
  const isClosed = prospect?.status === 'CLOSED'
  const isPromoted = prospect?.status === 'PROMOTED'
  const actionsDisabled = isClosed || isPromoted

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, display: 'flex', flexDirection: 'column' } }}
    >
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Patient Prospect</Typography>
          <Typography variant="h6">{prospect?.name || <em>(no name)</em>}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {prospect?.mobile}
            {prospect?.email ? ` · ${prospect.email}` : ''}
          </Typography>
          <Box sx={{ mt: 0.75 }}>{statusChip(prospect?.status)}</Box>
        </Box>
        <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : error ? (
          <Box sx={{ p: 3, color: 'error.main' }}>{error}</Box>
        ) : prospect ? (
          <>
            <Section title="Pathway">
              <Field label="Pathway" value={prospect.pathway} />
              <Field label="Specialty tag" value={prospect.specialtyTag} />
              <Field label="Subject type" value={prospect.subjectType} />
              <Field label="Source" value={prospect.source} />
              <Field label="Submitted" value={prospect.createdAt ? new Date(prospect.createdAt).toLocaleString() : null} />
              <Field label="Updated" value={prospect.updatedAt ? new Date(prospect.updatedAt).toLocaleString() : null} />
            </Section>

            {reco ? (
              <Section title="Initial recommendation">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {reco.userFacingSummary || reco.primaryRecommendation}
                </Typography>
                {reco.rationale ? (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{reco.rationale}</Typography>
                ) : null}
              </Section>
            ) : null}

            <Section title="Intake answers">
              <Field label="Presenting concerns" value={intake.presentingConcerns} />
              <Field label="Duration" value={intake.duration} />
              <Field label="Prior support" value={intake.priorSupportHistory} />
              <Field label="In their own words" value={intake.freeText} multiline />
              <Field label="Medical history" value={intake.medicalHistorySummary} multiline />
              <Field label="Preferred clinic hint" value={prospect.preferredClinicHint || intake.preferredClinicHint} />
              <Field label="City" value={intake.city} />
              <Field label="Preferred language" value={intake.preferredLanguage} />
              {subject ? (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Subject</Typography>
                  <Field label="Name" value={subject.name} />
                  <Field label="DOB" value={subject.dob} />
                  <Field label="Gender" value={subject.gender} />
                </Box>
              ) : null}
              {Object.keys(intake).length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  Nothing yet — the user hasn&rsquo;t added intake details after signup.
                </Typography>
              ) : null}
            </Section>

            <Section title={`Reports (${reports.length})`}>
              {reports.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No reports uploaded.
                </Typography>
              ) : (
                <Box sx={{ display: 'grid', gap: 1 }}>
                  {reports.map((r) => (
                    <Box
                      key={r.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: 1,
                        alignItems: 'center',
                        p: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                          {r.fileName || 'Untitled'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {r.contentType || 'file'}
                          {r.sizeBytes ? ` · ${(r.sizeBytes / 1024).toFixed(0)} KB` : ''}
                          {r.uploadedAt ? ` · ${new Date(r.uploadedAt).toLocaleDateString()}` : ''}
                        </Typography>
                      </Box>
                      {r.mediaId ? (
                        <Button
                          size="small"
                          startIcon={<LaunchIcon fontSize="small" />}
                          disabled={openingReportId === r.id}
                          onClick={() => openReport(prospect.userId, r.id)}
                        >
                          {openingReportId === r.id ? 'Opening…' : 'Open'}
                        </Button>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              )}
            </Section>

            <Section title="Care case">
              <Field label="Case id" value={careCase?.id} />
              <Field label="Status" value={careCase?.status} />
              <Field label="Booking status" value={careCase?.bookingStatus} />
              <Field label="Provisional pathway" value={careCase?.provisionalPathway} />
              <Field label="Urgency" value={careCase?.urgencyLevel} />
              <Field label="Recommended next step" value={careCase?.recommendedNextStep} />
            </Section>

            {prospect?.mobile ? (
              <Section title="Reach out">
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    startIcon={<WhatsAppIcon />}
                    component="a"
                    target="_blank"
                    rel="noreferrer"
                    href={`https://wa.me/${prospect.mobile.replace(/[^\d]/g, '')}`}
                    variant="outlined"
                  >
                    WhatsApp
                  </Button>
                  <Button
                    size="small"
                    startIcon={<PhoneIcon />}
                    component="a"
                    href={`tel:${prospect.mobile}`}
                    variant="outlined"
                  >
                    Call
                  </Button>
                </Box>
              </Section>
            ) : null}
          </>
        ) : null}
      </Box>

      <Divider />

      {/* Footer actions */}
      <Box sx={{ p: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="outlined"
          color="error"
          disabled={actionsDisabled}
          onClick={() => onReject?.(prospect)}
        >
          Close request
        </Button>
        <Button
          variant="contained"
          disabled={actionsDisabled}
          onClick={() => onPromote?.(prospect)}
        >
          Promote
        </Button>
      </Box>
    </Drawer>
  )
}
